import { useSelector } from "react-redux";
import * as _ from "lodash";
import { Dispatch, SetStateAction, useState } from "react";
import { useDeepCompareEffect } from "use-deep-compare";

import {
  ChartEntity,
  ChartList,
  ChartType,
  DataDescriptorEntity,
  Enterprise,
  EnterpriseAppState,
  OverlayViews
} from "@ctra/api";

import { useDataDictionary } from "@base";

type Result = Partial<Record<ChartType, ChartEntity["id"][]>>;

/**
 * Get a list of available chart views in a list of metrics
 * @param {Array<DataDescriptorEntity["id"]>} metricVariantIDList
 * @param {ChartType} defaultView
 * @returns {{views: Array<ChartType>, currentView: ChartType, setCurrentView: React.Dispatch<React.SetStateAction<ChartType>>, charts: ChartEntity[]}}
 */
export const useViews = (
  metricVariantIDList: Array<DataDescriptorEntity["id"]>,
  defaultView?: ChartType
): {
  views: Array<ChartType>;
  currentView: ChartType;
  setCurrentView: Dispatch<SetStateAction<ChartType>>;
  charts: ChartEntity[];
} => {
  const { dataDescriptors } = useDataDictionary();

  /**
   * Resolve the variants from the metric variant id list
   * @type {DataDescriptorEntity[]}
   */
  const metricVariants: DataDescriptorEntity[] = _.values(_.pick(dataDescriptors, metricVariantIDList));

  /**
   * Make a list of all supported charts
   * @type {ChartEntity["id"][]}
   */
  const supportedCharts: ChartEntity["id"][] = _.uniq(_.concat(..._.map(metricVariants, "supportedCharts")));

  /**
   * Get the corresponding charts from the store
   * @type {ChartEntity[]}
   */
  const chartEntities: ChartEntity[] = useSelector<EnterpriseAppState, ChartEntity[]>((state) => {
    const chartList = supportedCharts
      ? (Enterprise.entities.getChart(state, { id: supportedCharts }) as ChartList)
      : {};

    return _.map(supportedCharts, (id) => chartList[id]);
  });

  /**
   * Group the charts by view
   * @type {Dictionary<[ChartEntity, ...ChartEntity[]]>}
   */
  const viewGroups = _.groupBy(chartEntities, "view");

  /**
   * Make a view -> chart[] map
   * @type {{[p: string]: any[]}}
   */
  const map = _.mapValues(viewGroups, (entities) => _.map(entities, "id"));

  /**
   * Omit any view from the map which does not support all metrics
   * @note implicitly checking: the count of the chart list per view should be equal to the count of the metric variant list
   * @type {Dictionary<any[]>}
   */
  const result: Result = _.omitBy(map, (chartIDList) => _.size(chartIDList) !== _.size(metricVariantIDList));

  if (!_.size(result)) {
    throw new Error(`None of the given metrics seem to support the same view.`);
  }

  /**
   * Get oll the available views
   * @type {ChartType[]}
   */
  const viewOptions = _.keys(result) as ChartType[];

  /**
   * Check if the default view is supported
   * @type {undefined | boolean}
   */
  const isDefaultSupported = defaultView && _.includes(viewOptions, defaultView);

  /**
   * Provide a basic view-switch API
   */
  const [view, setView] = useState<ChartType>(
    isDefaultSupported ? defaultView : (_.first(_.keys(result)) as ChartType)
  );

  /**
   * Default chart list for the view
   * @note we use a _.compact because when charts are share some items in the entity list may be undefined
   * @type {ChartEntity[]}
   */
  const chartsForView = _.filter(_.compact(chartEntities), ({ id }) => _.includes(result[view], id));

  /**
   * Store the chart list
   */
  const [charts, setCharts] = useState<ChartEntity[]>(chartsForView);

  /**
   * Update the view when the default view changes or when the result changes
   */
  useDeepCompareEffect(() => {
    const isCurrentViewSupported = view && _.includes(viewOptions, view);

    if (!isCurrentViewSupported) {
      setView(_.first(_.keys(result)) as ChartType);
    }
  }, [result]);

  useDeepCompareEffect(() => {
    const chartsForView = _.filter(_.compact(chartEntities), ({ id }) => _.includes(result[view], id));

    if (!_.isEmpty(chartsForView)) {
      setCharts(chartsForView);
    }
  }, [view, chartEntities, metricVariantIDList]);

  /**
   * Enable only views that can be used in overlays when the user has more than one chart
   * @type {ChartType[]}
   */
  const enabledViews: ChartType[] = (
    _.size(metricVariantIDList) > 1 ? _.intersection(OverlayViews, _.keys(result)) : _.keys(result)
  ) as ChartType[];

  /**
   * Only keep the views that are supported by all metric variants
   */
  return {
    charts,
    views: enabledViews,
    currentView: view,
    setCurrentView: setView
  };
};
